import type React from 'react'
import type { IWrapper } from 'atoms/WrapperBlock'
import type { BANNER_ASPECT_RATIO } from 'vars/banner-aspect-ratio'
import type { BANNER_BACK_BUTTON_INDENT } from 'vars/banner-back-button-indent'
import type { COLOR } from 'vars/color'

import { useEffect, useRef } from 'react'
import { PosterContentSC } from 'blocks/PosterContent/PosterContent.styled'
import { BANNER_PADDINGS } from 'vars/banner-indents'
import { BANNER_RESERVED_HEIGHT_PROPERTY } from './PosterContent.h'

export const PosterContent = ({
  children,
  paddings = BANNER_PADDINGS.SIZE_10,
  bannerDescription = false,
  ...rest
}: IWrapper & {
  children: React.ReactNode
  backButtonIndent?: BANNER_BACK_BUTTON_INDENT
  aspectRatio?: BANNER_ASPECT_RATIO
  paddings?: BANNER_PADDINGS
  backgroundColor: COLOR
  /** Foreground hosts a banner-description block, which owns its own paddings. */
  bannerDescription?: boolean
}) => {
  const ref = useRef<HTMLDivElement>(null)

  /*
   * This block reserves the space the details banner takes in the page, and a
   * long title grows it past the banner aspect ratio. The poster and trailer
   * shells are out-of-flow siblings sized from that ratio alone, so they would
   * stop short and leave the page background showing above the details
   * section. Publishing the reserved height on the shared parent — custom
   * properties inherit to every child — lets those shells match it.
   */
  useEffect(() => {
    const element = ref.current
    const parent = element?.parentElement
    if (!bannerDescription || !element || !parent) return

    const publish = () =>
      parent.style.setProperty(
        BANNER_RESERVED_HEIGHT_PROPERTY,
        `${element.getBoundingClientRect().height}px`
      )

    publish()
    const observer = new ResizeObserver(publish)
    observer.observe(element)

    return () => {
      observer.disconnect()
      parent.style.removeProperty(BANNER_RESERVED_HEIGHT_PROPERTY)
    }
  }, [bannerDescription])

  return (
    <PosterContentSC
      ref={ref}
      paddings={paddings}
      bannerDescription={bannerDescription}
      {...rest}
    >
      {children}
    </PosterContentSC>
  )
}
